import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ShieldAlert, RefreshCw, KeyRound, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import apiService from "@/services/api";
import { getCurrentUser } from "@/data/users";

const ISSUE_LABEL = { no_password: "Cannot log in", duplicate: "Duplicate account" };

const IssueRow = ({ row, defaults, onReset }) => {
  const [pw, setPw] = useState(defaults[row.role] || "Teacher@2024");
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try { await onReset(row.id, pw); } finally { setBusy(false); }
  };
  return (
    <tr className="border-b last:border-0 hover:bg-amber-50/40" data-testid={`login-health-row-${row.id}`}>
      <td className="py-2 pr-3">
        <div className="font-medium text-slate-900">{row.name}</div>
        <div className="text-xs text-slate-500">{row.email}</div>
      </td>
      <td className="py-2 pr-3 text-sm text-slate-600 capitalize">{row.role}</td>
      <td className="py-2 pr-3 text-sm text-slate-600">{row.school || "—"}</td>
      <td className="py-2 pr-3">
        <Badge variant={row.issue === "no_password" ? "destructive" : "secondary"} data-testid={`login-health-issue-${row.id}`}>{ISSUE_LABEL[row.issue]}</Badge>
        <div className="text-xs text-slate-500 mt-1">{row.detail}{row.role === "student" ? ` · ${row.classes} classes` : ""}</div>
      </td>
      <td className="py-2">
        {row.issue === "no_password" && (
          <div className="flex gap-2 items-center">
            <Input value={pw} onChange={(e) => setPw(e.target.value)} className="h-8 w-40 text-sm" data-testid={`login-health-pw-${row.id}`} />
            <Button size="sm" onClick={reset} disabled={busy} data-testid={`login-health-reset-${row.id}`}>
              <KeyRound className="h-3.5 w-3.5 mr-1" /> Set password
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default function LoginHealthPage() {
  const isAdmin = (getCurrentUser()?.email || "").toLowerCase() === "roytari@spc.edu.vu";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { setData(await apiService.request('/api/admin/login-health')); }
    catch (e) { toast.error(e.message || "Could not load login health"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const onReset = async (user_id, new_password) => {
    try {
      const r = await apiService.request('/api/admin/login-health/reset-password', { method: 'POST', body: JSON.stringify({ user_id, new_password }) });
      toast.success(`Password set for ${r.email}`);
      await load();
    } catch (e) { toast.error(e.message || "Reset failed"); }
  };

  const issues = (data?.issues || []).filter(i => filter === "all" || i.issue === filter);
  const counts = { no_password: 0, duplicate: 0 };
  (data?.issues || []).forEach(i => { counts[i.issue] += 1; });

  if (!isAdmin) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="login-health-denied">Only the portal admin can view the Login Health Check.</div>;
  }

  return (
    <div className="space-y-6" data-testid="login-health-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldAlert className="h-6 w-6 text-amber-600" /> Login Health Check</h1>
          <p className="text-sm text-slate-600 mt-1">Accounts that will fail to log in — fix them before students or teachers report it.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="login-health-refresh"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Re-check</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[["all", "Accounts checked", data?.checked ?? "—", Users, "text-slate-700"],
          ["no_password", "Cannot log in", counts.no_password, KeyRound, "text-red-600"],
          ["duplicate", "Duplicate accounts", counts.duplicate, Users, "text-amber-600"]].map(([key, label, value, Icon, color]) => (
          <button key={key} onClick={() => setFilter(key)} className={`text-left rounded-lg border p-4 bg-white transition-colors ${filter === key ? "border-slate-900" : "border-slate-200 hover:border-slate-400"}`} data-testid={`login-health-stat-${key}`}>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">{label}</span><Icon className={`h-4 w-4 ${color}`} /></div>
            <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Problem accounts</CardTitle>
          <CardDescription>Pupils at schools where students never log in (primary, Ambaebulu JSS) are skipped — their parents log in instead. Students default to Student@2024, staff to Teacher@2024.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading && !data ? <p className="text-sm text-slate-500">Checking every account…</p>
            : issues.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="login-health-all-clear"><CheckCircle2 className="h-4 w-4" /> {filter === "all" ? "Every account can log in." : "No accounts with this problem."}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead><tr className="text-xs uppercase text-slate-500 border-b"><th className="py-2 pr-3">Account</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">School</th><th className="py-2 pr-3">Problem</th><th className="py-2">Fix</th></tr></thead>
                  <tbody>{issues.map(r => <IssueRow key={`${r.id}-${r.issue}`} row={r} defaults={data.defaults} onReset={onReset} />)}</tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
